<template>
  <div class="search-page">
    <!-- 顶部标题栏（PC端样式） -->
    <div class="search-header">
      <i class="el-icon-arrow-left back-btn" @click="goBack"></i>
      <span class="header-title">搜索</span>
    </div>

    <!-- 搜索框 -->
    <div class="search-bar">
      <div class="search-input-wrap">
        <i class="el-icon-search search-icon"></i>
        <input
          ref="searchInput"
          class="search-input"
          v-model="keyword"
          @input="onInputChange"
          @keyup.enter="onSearch"
          placeholder="搜索好友、群聊、聊天记录、文件"
        />
        <i v-if="keyword" class="el-icon-close clear-icon" @click="clearKeyword"></i>
      </div>
      <button class="search-btn" @click="onSearch">搜索</button>
    </div>

    <!-- 搜索内容 -->
    <div class="search-content">
      <!-- 有结果 -->
      <div v-if="hasResults" class="results-wrap">
        <!-- 好友结果 -->
        <div v-if="friendResults.length > 0" class="result-section">
          <div class="section-title">
            <i class="el-icon-user"></i>
            <span>好友</span>
            <span class="section-count">{{ friendResults.length }}</span>
          </div>
          <div
            v-for="item in friendResults"
            :key="'friend-' + item.id"
            class="result-item"
            @click="onFriendClick(item)"
          >
            <div class="item-avatar">
              <el-avatar :size="40" :src="item.headImage"></el-avatar>
            </div>
            <div class="item-info">
              <div class="item-name" v-html="highlightKeyword(item.name || item.showNickName || item.nickName)"></div>
              <div class="item-extra" v-if="item.content">{{ item.content }}</div>
            </div>
          </div>
        </div>

        <!-- 群聊结果 -->
        <div v-if="groupResults.length > 0" class="result-section">
          <div class="section-title">
            <i class="el-icon-files"></i>
            <span>群聊</span>
            <span class="section-count">{{ groupResults.length }}</span>
          </div>
          <div
            v-for="item in groupResults"
            :key="'group-' + item.id"
            class="result-item"
            @click="onGroupClick(item)"
          >
            <div class="item-avatar">
              <el-avatar :size="40" :src="item.headImage || item.headImageThumb" shape="square"></el-avatar>
            </div>
            <div class="item-info">
              <div class="item-name" v-html="highlightKeyword(item.name || item.showGroupName)"></div>
              <div class="item-extra">{{ item.memberCount || item.memberCnt || 0 }} 位成员</div>
            </div>
          </div>
        </div>

        <!-- 聊天记录结果 -->
        <div v-if="messageResults.length > 0" class="result-section">
          <div class="section-title">
            <i class="el-icon-chat-line-round"></i>
            <span>聊天记录</span>
            <span class="section-count">{{ messageResults.length }}</span>
          </div>
          <div
            v-for="item in messageResults"
            :key="'msg-' + item.id"
            class="result-item message-item"
            @click="onMessageClick(item)"
          >
            <div class="item-avatar">
              <el-avatar :size="36" :src="item.sendHeadImage">
                {{ item.sendNickName ? item.sendNickName.substring(0, 1) : '?' }}
              </el-avatar>
            </div>
            <div class="item-info">
              <div class="item-name">{{ item.sendNickName || '我' }}</div>
              <div class="item-preview" v-html="highlightKeyword(item.content)"></div>
            </div>
            <div class="item-time">
              <span>{{ formatRelativeDate(item.sendTime) }}</span>
            </div>
          </div>
        </div>

        <!-- 文件结果 -->
        <div v-if="fileResults.length > 0" class="result-section">
          <div class="section-title">
            <i class="el-icon-document"></i>
            <span>文件</span>
            <span class="section-count">{{ fileResults.length }}</span>
          </div>
          <div
            v-for="item in fileResults"
            :key="'file-' + item.id"
            class="result-item message-item"
            @click="onFileClick(item)"
          >
            <div class="item-avatar file-icon">
              <i class="el-icon-document"></i>
            </div>
            <div class="item-info">
              <div class="item-name">{{ item.chatName || '未知' }}</div>
              <div class="item-preview">[文件] {{ getFileName(item) }}</div>
            </div>
            <div class="item-time">
              <span>{{ formatRelativeDate(item.sendTime) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 加载中 -->
      <div v-else-if="loading" class="loading-wrap">
        <div class="loading-spinner"></div>
        <span>搜索中...</span>
      </div>

      <!-- 无结果（只有在已搜索且有关键词时才显示） -->
      <div v-else-if="searched && keyword" class="empty-tip">
        <i class="el-icon-search"></i>
        <span>未找到相关结果</span>
      </div>

      <!-- 无关键词时显示空状态 -->
      <div v-else-if="!keyword" class="empty-tip">
        <i class="el-icon-search"></i>
        <span>输入关键词搜索好友、群聊、聊天记录或文件</span>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: "Search",
  data() {
    return {
      keyword: "",
      loading: false,
      searched: false,

      // 原始数据（从 store 加载）
      allFriends: [],
      allGroups: [],

      // 过滤后的数据
      friendResults: [],
      groupResults: [],
      messageResults: [],
      fileResults: [],

      // 点击搜索时从后端获取的聊天记录和文件
      serverMessages: [],
      serverFiles: []
    };
  },
  computed: {
    hasResults() {
      return this.friendResults.length > 0 ||
        this.groupResults.length > 0 ||
        this.messageResults.length > 0 ||
        this.fileResults.length > 0;
    }
  },
  mounted() {
    this.loadInitialData();
    this.$nextTick(() => {
      this.$refs.searchInput && this.$refs.searchInput.focus();
    });
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    async loadInitialData() {
      // 加载好友列表
      if (this.friendStore.friends && this.friendStore.friends.length > 0) {
        this.allFriends = this.friendStore.friends.filter(f => !f.deleted);
      } else {
        try {
          await this.friendStore.loadFriend();
          this.allFriends = this.friendStore.friends.filter(f => !f.deleted);
        } catch (e) {
          this.allFriends = [];
        }
      }

      // 加载群聊列表
      if (this.groupStore.groups && this.groupStore.groups.length > 0) {
        this.allGroups = this.groupStore.groups.filter(g => !g.quit);
      } else {
        try {
          await this.groupStore.loadGroup();
          this.allGroups = this.groupStore.groups.filter(g => !g.quit);
        } catch (e) {
          this.allGroups = [];
        }
      }
    },
    clearKeyword() {
      this.keyword = "";
      this.friendResults = this.allFriends;
      this.groupResults = this.allGroups;
      this.messageResults = [];
      this.fileResults = [];
      this.serverMessages = [];
      this.serverFiles = [];
      this.searched = false;
      this.$refs.searchInput && this.$refs.searchInput.focus();
    },
    filterFriends(keyword) {
      if (!keyword) {
        return [...this.allFriends];
      }
      const kw = keyword.toLowerCase();
      return this.allFriends.filter(f => {
        const name = (f.name || f.showNickName || f.nickName || '').toLowerCase();
        return name.includes(kw);
      });
    },
    filterGroups(keyword) {
      if (!keyword) {
        return [...this.allGroups];
      }
      const kw = keyword.toLowerCase();
      return this.allGroups.filter(g => {
        const name = (g.name || g.showGroupName || '').toLowerCase();
        return name.includes(kw);
      });
    },
    filterMessages(keyword) {
      if (!keyword) {
        return [...this.serverMessages];
      }
      const kw = keyword.toLowerCase();
      return this.serverMessages.filter(m => {
        const content = (m.content || '').toLowerCase();
        return content.includes(kw);
      });
    },
    filterFiles(keyword) {
      if (!keyword) {
        return [...this.serverFiles];
      }
      const kw = keyword.toLowerCase();
      return this.serverFiles.filter(f => {
        const name = (this.getFileName(f) || '').toLowerCase();
        return name.includes(kw);
      });
    },
    onInputChange() {
      const keyword = this.keyword.trim();
      this.searched = !!keyword;
      // 输入时前端过滤好友和群聊
      this.friendResults = this.filterFriends(keyword);
      this.groupResults = this.filterGroups(keyword);
      // 如果有关键词且已经搜索过，同时过滤聊天记录和文件
      if (keyword && this.serverMessages.length > 0) {
        this.messageResults = this.filterMessages(keyword);
        this.fileResults = this.filterFiles(keyword);
      }
    },
    onSearch() {
      const keyword = this.keyword.trim();
      if (!keyword) {
        this.friendResults = [...this.allFriends];
        this.groupResults = [...this.allGroups];
        this.messageResults = [];
        this.fileResults = [];
        return;
      }

      this.loading = true;
      this.searched = true;

      // 先更新前端过滤的好友和群聊
      this.friendResults = this.filterFriends(keyword);
      this.groupResults = this.filterGroups(keyword);

      // 使用 searchAll 接口搜索聊天记录和文件
      this.$http({
        url: '/search/all',
        method: 'GET',
        params: { keyword: keyword }
      }).then(data => {
        // 合并聊天记录（去重）
        const newMessages = data.messages || [];
        const existingMsgIds = new Set(this.serverMessages.map(m => m.id));
        const uniqueMessages = newMessages.filter(m => !existingMsgIds.has(m.id));
        this.serverMessages = [...this.serverMessages, ...uniqueMessages];
        this.messageResults = this.filterMessages(keyword);

        // 合并文件（去重）
        const newFiles = data.files || [];
        const existingFileIds = new Set(this.serverFiles.map(f => f.id));
        const uniqueFiles = newFiles.filter(f => !existingFileIds.has(f.id));
        this.serverFiles = [...this.serverFiles, ...uniqueFiles];
        this.fileResults = this.filterFiles(keyword);
      }).catch(() => {
        this.$message.error('搜索失败');
      }).finally(() => {
        this.loading = false;
      });
    },
    highlightKeyword(text) {
      if (!text || !this.keyword) return text;
      const regex = new RegExp(`(${this.escapeRegex(this.keyword)})`, 'gi');
      return text.replace(regex, '<span class="highlight">$1</span>');
    },
    escapeRegex(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    formatRelativeDate(time) {
      if (!time) return '';
      const date = new Date(time);
      const now = new Date();

      const dateYear = date.getFullYear();
      const dateMonth = date.getMonth();
      const dateDay = date.getDate();
      const nowYear = now.getFullYear();
      const nowMonth = now.getMonth();
      const nowDay = now.getDate();

      // 今天
      if (dateYear === nowYear && dateMonth === nowMonth && dateDay === nowDay) {
        return '今天';
      }

      // 昨天
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      if (dateYear === yesterday.getFullYear() && dateMonth === yesterday.getMonth() && dateDay === yesterday.getDate()) {
        return '昨天';
      }

      // 之前显示日期
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    getFileName(item) {
      // 优先使用name字段，如果没有则解析content中的JSON
      if (item.name) return item.name;
      if (item.fileName) return item.fileName;
      if (item.content) {
        try {
          const content = typeof item.content === 'string' ? JSON.parse(item.content) : item.content;
          return content.name || '未知文件';
        } catch (e) {
          return item.content;
        }
      }
      return '未知文件';
    },
    getFileSize(item) {
      // 优先使用fileSize字段，如果没有则解析content中的JSON
      if (item.fileSize) return item.fileSize;
      if (item.content) {
        try {
          const content = typeof item.content === 'string' ? JSON.parse(item.content) : item.content;
          return content.size || 0;
        } catch (e) {
          return 0;
        }
      }
      return 0;
    },
    onFriendClick(item) {
      // 跳转到对应好友的聊天页面
      this.openChatAndNavigate("PRIVATE", item.id, item.name, item.headImage);
    },
    onGroupClick(item) {
      // 跳转到对应群聊的聊天页面
      this.openChatAndNavigate("GROUP", item.id, item.name, item.headImage);
    },
    onMessageClick(item) {
      // 跳转到聊天页面，并定位到指定消息
      const chatType = item.chatType === "PRIVATE" ? "PRIVATE" : "GROUP";
      this.openChatAndNavigate(chatType, item.chatId, item.chatName, item.headImage, item.id);
    },
    onFileClick(item) {
      // 跳转到聊天页面，并定位到指定文件消息
      const chatType = item.chatType === "PRIVATE" ? "PRIVATE" : "GROUP";
      this.openChatAndNavigate(chatType, item.chatId, item.chatName, item.headImage, item.id);
    },
    openChatAndNavigate(type, targetId, showName, headImage, messageId) {
      // 先打开聊天会话
      const chatInfo = {
        type: type,
        targetId: targetId,
        showName: showName,
        headImage: headImage
      };
      this.chatStore.openChat(chatInfo);
      const chat = this.chatStore.findChat(chatInfo);
      if (!chat) {
        this.$message.error("打开会话失败");
        return;
      }
      // 跳转到聊天页面
      let url = "/home/chat";
      const query = [];
      if (type === "PRIVATE") {
        query.push("friendId=" + targetId);
      } else {
        query.push("groupId=" + targetId);
      }
      if (messageId) {
        query.push("messageId=" + messageId);
      }
      this.$router.push(url + "?" + query.join("&"));
    }
  }
};
</script>

<style lang="scss" scoped>
.search-page {
  height: 100%;
  width: 100%;
  max-width: 400px;
  margin-left: 0;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.04);
  text-align: left;

  // 顶部标题栏
  .search-header {
    height: 56px;
    display: flex;
    align-items: center;
    padding: 0 16px;
    background-color: #fff;
    border-bottom: 1px solid #e8e8e8;
    flex-shrink: 0;

    .back-btn {
      font-size: 20px;
      cursor: pointer;
      color: #666;
      padding: 4px 8px;

      &:hover {
        color: #409eff;
      }
    }

    .header-title {
      font-size: 16px;
      font-weight: 600;
      color: #333;
      margin-left: 8px;
    }
  }

  // 搜索框
  .search-bar {
    background-color: #fff;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;

    .search-input-wrap {
      flex: 1;
      height: 36px;
      display: flex;
      align-items: center;
      padding: 0 12px;
      background-color: #f5f5f5;
      border-radius: 18px;
      transition: background-color 0.2s;

      &:focus-within {
        background-color: #fff;
        box-shadow: 0 0 0 1px #409eff;
      }

      .search-icon {
        color: #999;
        font-size: 16px;
        margin-right: 8px;
      }

      .search-input {
        flex: 1;
        border: none;
        outline: none;
        background: transparent;
        font-size: 14px;
        color: #333;

        &::placeholder {
          color: #999;
        }
      }

      .clear-icon {
        color: #999;
        cursor: pointer;
        font-size: 16px;
        margin-left: 8px;
        transition: color 0.2s;

        &:hover {
          color: #666;
        }
      }
    }

    .search-btn {
      height: 36px;
      padding: 0 18px;
      background-color: #1f86d2;
      color: #fff;
      font-size: 14px;
      border: none;
      border-radius: 18px;
      cursor: pointer;
      transition: opacity 0.2s;

      &:hover {
        opacity: 0.9;
      }

      &:active {
        opacity: 0.8;
      }
    }
  }

  // 搜索内容
  .search-content {
    flex: 1;
    overflow-y: auto;
    padding: 12px 16px;

    .results-wrap {
      .result-section {
        background-color: #fff;
        border-radius: 8px;
        margin-bottom: 12px;
        overflow: hidden;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

        .section-title {
          display: flex;
          align-items: center;
          padding: 10px 14px;
          font-size: 13px;
          color: #666;
          background-color: #fafafa;
          border-bottom: 1px solid #f0f0f0;

          i {
            margin-right: 8px;
            font-size: 14px;
            color: #409eff;
          }

          span:first-of-type {
            font-weight: 500;
          }

          .section-count {
            margin-left: auto;
            font-size: 12px;
            color: #999;
            background-color: #f0f0f0;
            padding: 2px 8px;
            border-radius: 10px;
          }
        }

        .result-item {
          display: flex;
          align-items: center;
          padding: 10px 14px;
          border-bottom: 1px solid #f5f5f5;
          cursor: pointer;
          transition: background-color 0.2s;

          &:last-child {
            border-bottom: none;
          }

          &:hover {
            background-color: #f5f5f5;
          }

          .item-avatar {
            flex-shrink: 0;
            margin-right: 12px;
          }

          .file-icon {
            width: 40px;
            height: 40px;
            background-color: #e8f4ff;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #409eff;
            font-size: 22px;
          }

          .item-info {
            flex: 1;
            min-width: 0;

            .item-name {
              font-size: 14px;
              color: #333;
              font-weight: 500;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;

              ::v-deep .highlight {
                color: #1f86d2;
                font-weight: bold;
              }
            }

            .item-extra,
            .item-preview {
              font-size: 12px;
              color: #999;
              margin-top: 2px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            .item-preview {
              ::v-deep .highlight {
                color: #1f86d2;
                font-weight: bold;
              }
            }
          }

          .item-time {
            display: flex;
            align-items: center;
            font-size: 12px;
            color: #999;
            margin-left: 12px;
            white-space: nowrap;
            flex-shrink: 0;
          }

          &.message-item {
            padding: 12px 14px;
            min-height: 60px;

            .item-info {
              min-height: 40px;
              display: flex;
              flex-direction: column;
              justify-content: center;

              .item-preview {
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                white-space: normal;
              }
            }
          }
        }
      }
    }

    .loading-wrap {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 60px 0;
      color: #999;
      font-size: 13px;

      .loading-spinner {
        width: 32px;
        height: 32px;
        border: 3px solid #e0e0e0;
        border-top-color: #1f86d2;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
        margin-bottom: 12px;
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
    }

    .empty-tip {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 80px 0;
      color: #999;
      font-size: 13px;

      i {
        font-size: 56px;
        margin-bottom: 12px;
        color: #ddd;
      }
    }
  }

  // 搜索提示
  .search-hint {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: #999;

    .hint-title {
      font-size: 14px;
      margin-bottom: 20px;
    }
  }
}
</style>
